"use client";

import { Box, Flex, Icon, Spinner } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import { Modal, ModalBody, Text } from "@/components/ui";
import { PUBLIC_AUTH_FORM_WIDTH } from "@/components/layout/public-layout-constants";
import { useThemeTokens } from "@/lib/theme/tokens";
import { getRoleDescription, getRoleDisplayName } from "@/lib/utils/roles";

interface RoleSelectionModalProps {
  isOpen: boolean;
  availableRoles: string[];
  onRoleSelect: (role: string) => Promise<void>;
  onClose: () => void;
}

export const RoleSelectionModal = ({
  isOpen,
  availableRoles,
  onRoleSelect,
  onClose,
}: RoleSelectionModalProps) => {
  const theme = useThemeTokens();
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedRole(null);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleRoleClick = async (role: string) => {
    if (isLoading) return;

    setSelectedRole(role);
    setIsLoading(true);

    try {
      await onRoleSelect(role);
    } catch (error) {
      console.error("Error selecting role:", error);
      setSelectedRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  const roleLabel =
    availableRoles.length === 1 ? "1 role" : `${availableRoles.length} roles`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: "calc(100% - 32px)", md: "md" }}
      contentProps={{
        maxW: { md: PUBLIC_AUTH_FORM_WIDTH },
        overflow: "hidden",
      }}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
    >
      <ModalBody p={0} maxH={{ base: "85dvh", md: "88dvh" }} overflowY="auto">
        <Box
          px={{ base: 5, md: 6 }}
          pt={{ base: 5, md: 6 }}
          pb={{ base: 4, md: 5 }}
          borderBottomWidth="1px"
          borderColor={theme.borderGlass.subtle}
        >
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            mb={2}
          >
            Admin access
          </Text>
          <Text
            as="h2"
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="600"
            letterSpacing="-0.03em"
            color={theme.text.primary}
            lineHeight="1.2"
            mb={2}
          >
            Choose your role
          </Text>
          <Text fontSize="sm" color={theme.text.secondary} lineHeight="1.65">
            You have access to more than one role. Pick how you&apos;d like to use the
            dashboard for this session.
          </Text>
        </Box>

        <Box px={{ base: 5, md: 6 }} pt={4}>
          <Text
            variant="tertiary"
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.1em"
            fontWeight="500"
            mb={3}
          >
            {roleLabel}
          </Text>
        </Box>

        <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
          {availableRoles.map((role, index) => {
            const isSelected = selectedRole === role;
            const isProcessing = isLoading && isSelected;
            const isDimmed = isLoading && !isSelected;
            const isLast = index === availableRoles.length - 1;

            return (
              <Box
                key={role}
                as="button"
                type="button"
                onClick={() => void handleRoleClick(role)}
                disabled={isLoading}
                w="100%"
                textAlign="left"
                bg="transparent"
                border="none"
                px={{ base: 5, md: 6 }}
                py={{ base: 4, md: 5 }}
                borderBottomWidth={isLast ? 0 : "1px"}
                borderColor={theme.borderGlass.subtle}
                cursor={isLoading ? "not-allowed" : "pointer"}
                opacity={isDimmed ? 0.5 : 1}
                transition="opacity 0.2s ease"
                outline="none"
                _focusVisible={{
                  boxShadow: theme.focusRing,
                  borderRadius: "8px",
                }}
                sx={{
                  "@media (hover: hover)": {
                    "&:hover:not(:disabled) [data-role-name]": {
                      color: theme.accent.primary,
                    },
                    "&:hover:not(:disabled) [data-role-chevron]": {
                      color: theme.accent.primary,
                    },
                  },
                }}
              >
                <Flex align="center" justify="space-between" gap={4}>
                  <Box flex={1} minW={0}>
                    <Text
                      data-role-name
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="600"
                      letterSpacing="-0.02em"
                      color={theme.text.primary}
                      lineHeight="1.25"
                      transition="color 0.2s ease"
                      mb={1}
                    >
                      {getRoleDisplayName(role)}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={theme.text.secondary}
                      lineHeight="1.5"
                      noOfLines={2}
                    >
                      {getRoleDescription(role)}
                    </Text>
                  </Box>

                  <Flex flexShrink={0} align="center" justify="center" w="20px">
                    {isProcessing ? (
                      <Spinner size="sm" color={theme.accent.primary} thickness="2px" />
                    ) : (
                      <Icon
                        as={HiOutlineChevronRight}
                        data-role-chevron
                        boxSize="16px"
                        color={theme.text.tertiary}
                        transition="transform 0.2s ease, color 0.2s ease"
                        aria-hidden
                      />
                    )}
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Box>

        <Box
          px={{ base: 5, md: 6 }}
          py={4}
          borderTopWidth="1px"
          borderColor={theme.borderGlass.subtle}
        >
          <Text
            fontSize="xs"
            color={theme.text.tertiary}
            textAlign="center"
            lineHeight="1.5"
          >
            You can switch roles anytime from the navigation bar.
          </Text>
        </Box>
      </ModalBody>
    </Modal>
  );
};
